import React, { useState } from 'react';
import classNames from 'classnames';

import { Modal, ResponsiveImage } from '../../components';
import ListingImageGallery from './ListingImageGallery/ListingImageGallery';

import css from './ListingPage.module.css';

// Phase 2: photo mosaic (Airbnb-style hero + 2x2 grid). Replaces the
// inline carousel. Clicking the hero, any tile, or "Show all photos"
// opens the existing ListingImageGallery carousel as a lightbox at the
// clicked photo's index (tile 3 -> photo 3). Mobile shows the hero only
// plus the "Show all photos" button; the lightbox carousel (swipeable,
// with prev/next arrows) is the full-gallery experience everywhere.
const SectionGallery = props => {
  const { listing, variantPrefix, onManageDisableScrolling, heroActions } = props;
  const images = Array.isArray(listing.images) ? listing.images : [];
  const imageVariants = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];
  const thumbnailVariants = [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`];
  const total = images.length;

  // index is the photo the lightbox opens on; open toggles the modal.
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const openAt = index => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox(prev => ({ ...prev, open: false }));

  if (total === 0) {
    return null;
  }

  const hero = images[0];
  const gridTiles = images.slice(1, 5); // up to four 2x2 tiles
  const altFor = i => `Photo ${i + 1} of ${total}`;

  return (
    <section
      className={classNames(css.mosaic, { [css.mosaicSingle]: gridTiles.length === 0 })}
      data-testid="carousel"
    >
      {/* Share / Save actions overlaid on the hero's top-right, mirroring
          the "Show all photos" button at the bottom-right. Absolutely
          positioned over .mosaic (which is position:relative). */}
      {heroActions ? <div className={css.mosaicActions}>{heroActions}</div> : null}

      <button
        type="button"
        className={css.mosaicHero}
        onClick={() => openAt(0)}
        aria-label={`Open photo 1 of ${total}`}
      >
        <ResponsiveImage
          rootClassName={css.mosaicImg}
          image={hero}
          alt={altFor(0)}
          variants={imageVariants}
          sizes="(max-width: 767px) 100vw, 50vw"
        />
      </button>

      {gridTiles.length > 0 && (
        <div className={classNames(css.mosaicGrid, css[`mosaicGrid${gridTiles.length}`])}>
          {gridTiles.map((img, idx) => (
            <button
              key={img.id?.uuid || idx}
              type="button"
              className={css.mosaicTile}
              onClick={() => openAt(idx + 1)}
              aria-label={`Open photo ${idx + 2} of ${total}`}
            >
              <ResponsiveImage
                rootClassName={css.mosaicImg}
                image={img}
                alt={altFor(idx + 1)}
                variants={thumbnailVariants}
                sizes="25vw"
              />
            </button>
          ))}
        </div>
      )}

      <button type="button" className={css.showAllPhotos} onClick={() => openAt(0)}>
        <span className={css.showAllPhotosIcon} aria-hidden="true">▦</span>
        Show all photos{total > 1 ? ` (${total})` : ''}
      </button>

      <Modal
        id="ListingPage.galleryLightbox"
        isOpen={lightbox.open}
        onClose={closeLightbox}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {lightbox.open && (
          // Keyed by index so each open remounts the carousel on the
          // clicked photo (react-image-gallery only reads startIndex on
          // mount).
          <ListingImageGallery
            key={lightbox.index}
            images={images}
            imageVariants={imageVariants}
            thumbnailVariants={thumbnailVariants}
            startIndex={lightbox.index}
            hideOpenButton
          />
        )}
      </Modal>
    </section>
  );
};

export default SectionGallery;
